/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { UncontrolledTooltip } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import useApi from 'hooks/useApi';
import {
  paginateNotificationsMassive,
  setClearNotificationsMassiveList,
} from 'redux/actions/notificationActions';

import { ReactComponent as NotificationIcon } from 'assets/icon-notification.svg';
import { ReactComponent as FilterIcon } from 'assets/icon-filter.svg';
import { ReactComponent as FilterAppliedIcon } from 'assets/icon-filter-applied.svg';
import { ButtonIcon } from 'shared/components/Buttons';
import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { colors } from 'shared/defaultTheme';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';
import Pagination from 'shared/components/Pagination';
import FilterDropdownInput from 'shared/components/FilterDropdownInput';
import FilterDateSelect from 'shared/components/FilterDateSelect';
import MainSpinner from 'shared/components/MainSpinner';
import useNotification from 'hooks/useNotification';
import { TableNotification, TdAction, TdName, Td, Thead, Tbody, TBodyTr } from '../styles';

const PAGE_SIZE = 10;

const NotificationsMassiveTable = ({
  handleRefresh,
  notificationsMassiveData,
  loading,
  setOpenFormModal,
}) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isCustomer, mail } = useSelector((state) => state.auth);
  const notification = useSelector((state) => state.notification);
  const {
    notificationsMassive,
    currentNotificationsMassive,
    sortConfigMassive,
    searchMassiveValue,
    clearList,
    allNotificationsMassive,
  } = notification;
  const [columnSearch, setColumnSearch] = useState({
    name: undefined,
    date: undefined,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [SelectedRemoveNotifyMassive, setSelectedRemoveNotifyMassive] = useState(null);
  const [showConfirmRemoveNotifyMassive, setShowConfirmRemoveNotifyMassive] = useState(false);
  const [showSuccessRemove, setShowSuccessRemove] = useState();
  const [showErrorRemove, setShowErrorRemove] = useState(false);
  const [showFilterDate, setShowFilterDate] = useState(false);
  const [showFilterCreated, setShowFilterCreated] = useState(false);

  const { handleSelectNotificationReview } = useNotification();

  const [{ loading: loadingDelete }, deleteNotificationMassive] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );
  const onToggleErrorRemove = () => setShowErrorRemove((currentShow) => !currentShow);

  const onToggleSuccessRemove = () => setShowSuccessRemove((currentShow) => !currentShow);

  const onEdit = (currentNotificationMassive) => {
    handleSelectNotificationReview(currentNotificationMassive);
    const today = new Date();
    if (today >= new Date(currentNotificationMassive.date)) {
      history.push(`/notificacion_details`, { isClientNotify: false });
    } else {
      setOpenFormModal(true);
    }
  };

  const onRemove = (currentNotification) => {
    setSelectedRemoveNotifyMassive(currentNotification);
    setShowConfirmRemoveNotifyMassive(true);
  };

  const onOkSuccessRemove = () => {
    handleRefresh();
    setSelectedRemoveNotifyMassive(null);
    onToggleSuccessRemove();
  };

  const onCancelRemove = () => {
    setShowConfirmRemoveNotifyMassive(false);
    setSelectedRemoveNotifyMassive(null);
  };

  const removeSuccess = () => {
    try {
      if (SelectedRemoveNotifyMassive !== undefined && SelectedRemoveNotifyMassive !== null) {
        setShowConfirmRemoveNotifyMassive(false);
        onToggleSuccessRemove();
      }
    } catch (ex) {
      throw new Error(ex);
    }
  };

  const onOkRemove = async () => {
    try {
      const { clientNavisionId, id } = SelectedRemoveNotifyMassive;
      if (loadingDelete) {
        return;
      }
      const {
        data: { errors },
      } = await deleteNotificationMassive({
        url: `${window.API_V1}/gen/not/${clientNavisionId}/${encodeURIComponent(mail)}/${id}`,
      });
      if (errors.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      removeSuccess();
    } catch (ex) {
      setShowConfirmRemoveNotifyMassive(false);
      onToggleErrorRemove();
    }
  };

  const onFirstPage = () => {
    dispatch(paginateNotificationsMassive({ page: 1, pageSize: PAGE_SIZE }));
    setCurrentPage(1);
  };

  const onLastPage = () => {
    dispatch(paginateNotificationsMassive({ page: lastPage, pageSize: PAGE_SIZE }));
    setCurrentPage(lastPage);
  };

  const onNextPage = () => {
    const page = currentPage + 1;
    if (page > lastPage) {
      return;
    }
    dispatch(paginateNotificationsMassive({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onPreviousPage = () => {
    const page = currentPage - 1;
    if (page <= 0) {
      return;
    }
    dispatch(paginateNotificationsMassive({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onGoToPage = (page) => {
    dispatch(paginateNotificationsMassive({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  useEffect(() => {
    const currentLastPage = Math.ceil(notificationsMassive.length / PAGE_SIZE);
    onFirstPage();
    setLastPage(currentLastPage);
  }, [notificationsMassive]);

  const getOptionsFromAllNotifi = (fieldValue, fieldName) => {
    if (fieldValue === fieldName) {
      const values = Array.from(
        new Set(allNotificationsMassive.map((currentNotifi) => currentNotifi[fieldValue]))
      );
      return values.map((value) => ({ value, name: value }));
    }
    return allNotificationsMassive.map((currentNotifi) => ({
      value: currentNotifi[fieldValue],
      name: currentNotifi[fieldName],
    }));
  };

  const searchNotificationMassive = async (newColumnSearch) => {
    const searchParam =
      sortConfigMassive.length > 0
        ? {
            filters: [...sortConfigMassive],
            reduce: {
              limit: 0,
              skip: 0,
            },
          }
        : {
            filters: [],
            reduce: {
              limit: 0,
              skip: 0,
            },
          };
    Object.keys(newColumnSearch).forEach((key) => {
      if (newColumnSearch[key]) {
        searchParam.filters.push(newColumnSearch[key]);
      }
    });
    if (searchMassiveValue && searchMassiveValue.length >= 2) {
      searchParam.filterText = {
        regexFilter: searchMassiveValue,
      };
    }
    notificationsMassiveData(searchParam);
  };

  useEffect(() => {
    if (searchMassiveValue != null && !loading) {
      searchNotificationMassive(columnSearch);
    }
  }, [searchMassiveValue]);

  useEffect(() => {
    if (clearList) {
      setColumnSearch({
        name: undefined,
        date: undefined,
      });
      dispatch(setClearNotificationsMassiveList(false));
      searchNotificationMassive(columnSearch);
    }
  }, [clearList]);

  const onSearchOptionOrText = async ({
    selectedOption,
    searchText,
    fieldSelectedOption,
    fieldSearchText,
    column,
  }) => {
    const newColumnSearch = { ...columnSearch };
    newColumnSearch[column] = undefined;
    if (selectedOption) {
      newColumnSearch[column] = {
        model: 'GeneralNotify',
        field: fieldSelectedOption,
        filter: [selectedOption],
      };
    } else if (searchText) {
      newColumnSearch[column] = {
        model: 'GeneralNotify',
        field: fieldSearchText,
        filter: [searchText],
        isRegex: true,
      };
    }
    searchNotificationMassive(newColumnSearch);
    setColumnSearch(newColumnSearch);
  };

  const onSearchDate = async ({ dateFrom, dateTo, fieldSelectedOption, column }) => {
    const newColumnSearch = { ...columnSearch };
    newColumnSearch[column] = undefined;
    if (dateFrom && dateTo) {
      newColumnSearch[column] = {
        model: 'GeneralNotify',
        field: fieldSelectedOption,
        filter: [
          `${moment(dateFrom).format('YYYY-MM-DD')}T00:00:00.000Z`,
          `${moment(dateTo).format('YYYY-MM-DD')}T23:59:59.000Z`,
        ],
        IsLessThan: true,
      };
    }
    setShowFilterCreated(false);
    setShowFilterDate(false);
    searchNotificationMassive(newColumnSearch);
    setColumnSearch(newColumnSearch);
  };

  const handleTypeEventClick = (e, type, currentNotification) => {
    const { localName } = e.target;
    if (type === 'edit' && (localName === 'div' || localName === 'td')) {
      onEdit(currentNotification);
    }
    if (type === 'btnEdit') {
      onEdit(currentNotification);
    }
    if (type === 'delete') {
      onRemove(currentNotification);
    }
  };

  return (
    <div className="control-panel__table table-notifications">
      <TableNotification>
        <Thead>
          <tr>
            <th className="text-uppercase">
              {t('notification_column_issue')}
              <ButtonIcon
                title={t('general_button_filter')}
                id="filterNameColumn"
                className="button-icon"
                Icon={() =>
                  columnSearch.name ? (
                    <FilterAppliedIcon fill={colors.black} />
                  ) : (
                    <FilterIcon fill={colors.black} />
                  )
                }
                onClick={() => {
                  setShowFilterCreated(false);
                  setShowFilterDate(false);
                }}
              />
              <FilterDropdownInput
                dropdownOptions={getOptionsFromAllNotifi('name', 'name')}
                target="filterNameColumn"
                fieldSelectedOption="Name"
                fieldSearchText="Name"
                column="name"
                onSearch={onSearchOptionOrText}
                placement="bottom-start"
              />
            </th>
            <th className="text-uppercase">
              {t('notification_column_created_date')}
              <ButtonIcon
                title={t('general_button_filter')}
                id="filterCreatedColumn"
                className="button-icon"
                Icon={() =>
                  columnSearch.created ? (
                    <FilterAppliedIcon fill={colors.black} />
                  ) : (
                    <FilterIcon fill={colors.black} />
                  )
                }
                onClick={() => {
                  setShowFilterDate(false);
                }}
              />
              <FilterDateSelect
                dropdownOptions={getOptionsFromAllNotifi('created', 'created')}
                target="filterCreatedColumn"
                fieldSelectedOption="Created"
                column="created"
                onSearch={onSearchDate}
                showFilterDate={showFilterCreated}
                setShowFilterDate={setShowFilterCreated}
              />
            </th>
            <th className="text-uppercase">
              {t('notification_column_start_date')}
              <ButtonIcon
                title={t('general_button_filter')}
                id="filterDateColumn"
                className="button-icon"
                Icon={() =>
                  columnSearch.date ? (
                    <FilterAppliedIcon fill={colors.black} />
                  ) : (
                    <FilterIcon fill={colors.black} />
                  )
                }
                onClick={() => {
                  setShowFilterCreated(false);
                }}
              />
              <FilterDateSelect
                dropdownOptions={getOptionsFromAllNotifi('date', 'date')}
                target="filterDateColumn"
                fieldSelectedOption="Date"
                column="date"
                onSearch={onSearchDate}
                showFilterDate={showFilterDate}
                setShowFilterDate={setShowFilterDate}
              />
            </th>
            <th className="text-uppercase">{t('general_column_action')}</th>
          </tr>
        </Thead>
        <Tbody>
          {currentNotificationsMassive.map((currentNotification, index) => (
            <TBodyTr
              key={index}
              numAnswersUnread={currentNotification.numAnswersUnread}
              className="action-row-communication"
              onClick={(e) => handleTypeEventClick(e, 'edit', currentNotification)}
            >
              <TdName id={`notify-name-${index}`}>
                <div className="link-name-notification">
                  <p>{currentNotification.name}</p>
                </div>
                <UncontrolledTooltip
                  placement="top"
                  target={`notify-name-${index}`}
                  autohide={false}
                >
                  {currentNotification.name ?? ''}
                </UncontrolledTooltip>
              </TdName>
              <Td>{moment(currentNotification.created).format('DD/MM/YYYY HH:mm')}</Td>
              <Td>{moment(currentNotification.date).format('DD/MM/YYYY HH:mm')}</Td>
              <TdAction>
                <div className="d-flex">
                  <ButtonIcon
                    id={`edit-icon-notification-${index}`}
                    title={t('clients_edit_client')}
                    type="button"
                    Icon={() => <EditIcon fill={colors.suvaGrey} />}
                    className="button-icon"
                    onClick={(e) => handleTypeEventClick(e, 'btnEdit', currentNotification)}
                  />
                  {isCustomer && (
                    <>
                      <ButtonIcon
                        id={`trash-icon-notification-${index}`}
                        title={t('general_delete')}
                        type="button"
                        onClick={(e) => handleTypeEventClick(e, 'delete', currentNotification)}
                        Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                        className="button-icon mr-n3 ml-2"
                        isDisabled={currentNotification.answers?.length > 0}
                      />
                    </>
                  )}
                </div>
              </TdAction>
            </TBodyTr>
          ))}
        </Tbody>
      </TableNotification>
      {notificationsMassive.length === 0 && (
        <div className="panel_control__empty-folder d-flex flex-column align-items-center">
          <NotificationIcon fill={colors.suvaGrey} width="71" />
          <span>{t('notification_not_found')}</span>
        </div>
      )}
      {notificationsMassive.length > 0 && (
        <Pagination
          titlePagination={t('notification_title')}
          currentPage={currentPage}
          lastPage={lastPage}
          totalData={notificationsMassive.length}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onGoToPage={onGoToPage}
        />
      )}
      <ConfirmModal
        isOpen={showConfirmRemoveNotifyMassive}
        toggle={onCancelRemove}
        confirmMessage={t('notification_ask_delete_confirmation')}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelRemove}
        onOk={onOkRemove}
      />
      <AlertModal
        type="error"
        isOpen={showErrorRemove}
        toggle={onToggleErrorRemove}
        message={t('custom_field_delete_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleErrorRemove}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessRemove}
        toggle={onOkSuccessRemove}
        message={t('notification_delete_confirmation')}
        okText={t('general_button_ok')}
        onOk={onOkSuccessRemove}
      />
      {(loading || loadingDelete) && (
        <div className="d-flex justify-content-center mb-5 align-items-center h-100">
          <MainSpinner />
        </div>
      )}
    </div>
  );
};

NotificationsMassiveTable.propTypes = {
  handleRefresh: PropTypes.func.isRequired,
  notificationsMassiveData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setOpenFormModal: PropTypes.func.isRequired,
};

export default NotificationsMassiveTable;
